import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [allObjects, setAllObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    role: 'operator',
    branch_id: '',
    is_active: true,
    send_report: false,
  });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Access management modal state
  const [accessModalUser, setAccessModalUser] = useState(null);
  const [userAccessList, setUserAccessList] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [selectedObjectId, setSelectedObjectId] = useState('');
  const [selectedBranchIdForBatch, setSelectedBranchIdForBatch] = useState('');

  const perPage = 20;

  const fetchBranchesAndObjects = async () => {
    try {
      const [branchesRes, objectsRes] = await Promise.all([
        api.get('/branches', { params: { per_page: 100 } }),
        api.get('/objects', { params: { per_page: 500, active_only: true } }),
      ]);
      setBranches(branchesRes.data.items);
      setAllObjects(objectsRes.data.items);
    } catch (error) {
      console.error('Error fetching branches or objects:', error);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { per_page: perPage, page };
      if (search) params.search = search;

      const response = await api.get('/users', { params });
      setUsers(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchBranchesAndObjects();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openModal = (u = null) => {
    if (u) {
      setEditingUser(u);
      setFormData({
        username: u.username,
        password: '',
        email: u.email || '',
        full_name: u.full_name || '',
        role: u.role,
        branch_id: u.branch_id != null ? String(u.branch_id) : '',
        is_active: u.is_active,
        send_report: Boolean(u.send_report),
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        email: '',
        full_name: '',
        role: 'operator',
        branch_id: '',
        is_active: true,
        send_report: false,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email: formData.email.trim() || null,
        full_name: formData.full_name.trim() || null,
        role: formData.role,
        branch_id: formData.branch_id ? parseInt(formData.branch_id, 10) : null,
        is_active: formData.is_active,
        send_report: formData.send_report,
      };

      if (editingUser) {
        if (formData.password) {
          payload.password = formData.password;
        }
        await api.patch(`/users/${editingUser.id}`, payload);
      } else {
        payload.username = formData.username.trim();
        payload.password = formData.password;
        await api.post('/users', payload);
      }
      closeModal();
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const handleToggleActive = async (u) => {
    try {
      if (u.is_active) {
        await api.post(`/users/${u.id}/delete`);
      } else {
        await api.post(`/users/${u.id}/restore`);
      }
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка');
    }
  };

  // --- Access modal handlers ---
  const openAccessModal = async (u) => {
    setAccessModalUser(u);
    setSelectedObjectId('');
    setSelectedBranchIdForBatch('');
    await loadUserAccess(u.id);
  };

  const closeAccessModal = () => {
    setAccessModalUser(null);
    setUserAccessList([]);
  };

  const loadUserAccess = async (userId) => {
    setLoadingAccess(true);
    try {
      const res = await api.get(`/users/${userId}/access`);
      setUserAccessList(res.data);
    } catch (error) {
      console.error('Error fetching user access:', error);
      alert('Ошибка при получении доступов пользователя');
    } finally {
      setLoadingAccess(false);
    }
  };

  const handleAddObjectAccess = async () => {
    if (!selectedObjectId || !accessModalUser) return;
    try {
      await api.post(`/users/${accessModalUser.id}/access`, {
        user_id: accessModalUser.id,
        object_id: parseInt(selectedObjectId, 10),
        role_on_object: 'operator',
      });
      setSelectedObjectId('');
      await loadUserAccess(accessModalUser.id);
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка добавления доступа');
    }
  };

  const handleRemoveObjectAccess = async (accessId) => {
    if (!accessModalUser) return;
    try {
      await api.delete(`/users/${accessModalUser.id}/access/${accessId}`);
      await loadUserAccess(accessModalUser.id);
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка отзыва доступа');
    }
  };

  const handleAddBranchObjectsAccess = async () => {
    if (!selectedBranchIdForBatch || !accessModalUser) return;
    const branchIdNum = parseInt(selectedBranchIdForBatch, 10);
    const branchObjects = allObjects.filter((o) => o.branch_id === branchIdNum);

    if (branchObjects.length === 0) {
      alert('В этом филиале пока нет объектов.');
      return;
    }

    const currentObjectIds = new Set(userAccessList.map((a) => a.object_id));
    const toAdd = branchObjects.filter((o) => !currentObjectIds.has(o.id));

    if (toAdd.length === 0) {
      alert('У пользователя уже есть доступ ко всем объектам этого филиала.');
      return;
    }

    let addedCount = 0;
    for (const obj of toAdd) {
      try {
        await api.post(`/users/${accessModalUser.id}/access`, {
          user_id: accessModalUser.id,
          object_id: obj.id,
          role_on_object: 'operator',
        });
        addedCount++;
      } catch (err) {
        console.error('Error adding access to object:', obj.id, err);
      }
    }

    alert(`Добавлен доступ к ${addedCount} объектам филиала.`);
    setSelectedBranchIdForBatch('');
    await loadUserAccess(accessModalUser.id);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="users-page">
      <div className="page-header">
        <h2>Пользователи</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            className="btn-secondary"
            onClick={async () => {
              try {
                const response = await api.get('/shifts/export/rep-excel', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `rep_${new Date().toISOString().slice(0, 10)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
              } catch (err) {
                alert('Ошибка скачивания отчета rep.xlsx');
              }
            }}
            title="Скачать текущий суточный отчет в формате rep.xlsx по доступным филиалам"
          >
            📥 Скачать rep.xlsx
          </button>
          <button
            className="btn-secondary"
            onClick={async () => {
              if (!window.confirm('Отправить суточный отчет прямо сейчас всем подписчикам?')) return;
              try {
                const res = await api.post('/shifts/export/send-email');
                alert(`Рассылка завершена: успешно отправлено ${res.data.sent_count} из ${res.data.total_subscribers}`);
              } catch (err) {
                alert(err.response?.data?.detail || 'Ошибка отправки отчетов по email');
              }
            }}
            title="Принудительная отправка суточного отчета на email всем подписчикам"
          >
            ✉️ Разослать отчет
          </button>
          <button className="btn-primary" onClick={() => openModal()}>
            + Добавить пользователя
          </button>
        </div>
      </div>

      <div className="filters">
        <form className="search-form" onSubmit={(e) => { e.preventDefault(); setPage(1); fetchUsers(); }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по имени, email или логину..."
          />
          <button
            type="submit"
            className="btn-secondary"
          >
            Поиск
          </button>
        </form>
      </div>

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Логин</th>
                  <th>ФИО</th>
                  <th>Email</th>
                  <th>Роль</th>
                  <th>Филиал</th>
                  <th>Отчет</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">Нет пользователей</td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td><strong>{u.username}</strong></td>
                      <td>{u.full_name || '-'}</td>
                      <td>{u.email || '-'}</td>
                      <td><span className="badge">{u.role}</span></td>
                      <td>
                        {u.branch_name ? (
                          <span className="branch-badge">{u.branch_name}</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {u.send_report ? (
                          <span
                            className="badge"
                            style={{
                              background: u.email ? '#dcfce7' : '#fef9c3',
                              color: u.email ? '#15803d' : '#854d0e',
                              fontSize: '0.75rem',
                            }}
                            title={u.email ? `Отправка на ${u.email}` : 'Email не указан!'}
                          >
                            {u.email ? '✓ Вкл' : '⚠ Нет email'}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={`status ${u.is_active ? 'active' : 'inactive'}`}>
                          {u.is_active ? 'Активен' : 'Неактивен'}
                        </span>
                      </td>
                      <td className="actions">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => openModal(u)}
                          title="Редактировать профиль и филиал"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => openAccessModal(u)}
                          title="Управление доступом к объектам"
                        >
                          🔑
                        </button>
                        <button 
                          className="btn-icon" 
                          onClick={() => handleToggleActive(u)}
                          title={u.is_active ? 'Деактивировать' : 'Восстановить'}
                        >
                          {u.is_active ? '🔴' : '🟢'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button className="btn-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
                Назад
              </button>
              <span className="page-info">
                Страница {page} из {totalPages} ({total} пользователей)
              </span>
              <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                Вперёд
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingUser ? 'Редактировать пользователя' : 'Новый пользователь'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Логин *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={!!editingUser}
                />
              </div>
              <div className="form-group">
                <label>Пароль {!editingUser && '*'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={editingUser ? 'Оставьте пустым, если не меняется' : ''}
                  required={!editingUser}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>ФИО</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Роль</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="operator">Оператор</option>
                  <option value="manager">Менеджер</option>
                  <option value="admin">Администратор</option>
                </select>
              </div>

              <div className="form-group">
                <label>Привязка к филиалу</label>
                <select
                  value={formData.branch_id}
                  onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                >
                  <option value="">Не привязан</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} {b.code ? `(${b.code})` : ''}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#718096', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>
                  Привязка к филиалу автоматически предоставляет пользователю доступ ко всем его объектам.
                </small>
              </div>

              <div className="form-group checkbox-group" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 500, color: '#1e293b' }}>
                  <input
                    type="checkbox"
                    checked={formData.send_report}
                    onChange={(e) => setFormData({ ...formData, send_report: e.target.checked })}
                    style={{ width: '1.15rem', height: '1.15rem', accentColor: '#2563eb' }}
                  />
                  <span>Отправлять суточный отчет по окончании смены (Excel на email)</span>
                </label>
                <small style={{ color: '#64748b', fontSize: '0.8rem', display: 'block', marginTop: '0.35rem', marginLeft: '1.65rem' }}>
                  Отчет вида rep.xlsx формируется по окончании смены (в 21:00 MSK) строго по филиалам, к которым у пользователя есть доступ. Для отправки поле Email должно быть заполнено.
                </small>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  {' '}Активен
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {accessModalUser && (
        <div className="modal-overlay" onClick={closeAccessModal}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>
              Доступ к объектам: {accessModalUser.full_name || accessModalUser.username} ({accessModalUser.role})
            </h3>

            {accessModalUser.branch_name ? (
              <div className="access-note">
                🏢 Пользователь привязан к филиалу <strong>{accessModalUser.branch_name}</strong> и имеет доступ ко всем его объектам по умолчанию.
              </div>
            ) : (
              <div className="access-note" style={{ background: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' }}>
                ℹ️ Пользователь не привязан к конкретному филиалу. Доступ предоставляется индивидуально по объектам ниже.
              </div>
            )}

            <div style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#334155' }}>
              Индивидуальный доступ к объектам:
            </div>

            {loadingAccess ? (
              <div className="loading" style={{ margin: '1rem 0' }}>Загрузка доступов...</div>
            ) : userAccessList.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
                Нет персонально назначенных объектов.
              </p>
            ) : (
              <table className="access-table">
                <thead>
                  <tr>
                    <th>Код</th>
                    <th>Название объекта</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>Действие</th>
                  </tr>
                </thead>
                <tbody>
                  {userAccessList.map((access) => (
                    <tr key={access.id}>
                      <td>
                        {access.object_code ? (
                          <span className="object-code-badge">{access.object_code}</span>
                        ) : (
                          `#${access.object_id}`
                        )}
                      </td>
                      <td>
                        <strong>{access.object_name || `Объект #${access.object_id}`}</strong>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn-icon"
                          onClick={() => handleRemoveObjectAccess(access.id)}
                          title="Отозвать доступ"
                          style={{ color: '#ef4444' }}
                        >
                          ❌
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#334155' }}>
              Добавить доступ к объекту:
            </div>
            <div className="access-add-section">
              <select
                value={selectedObjectId}
                onChange={(e) => setSelectedObjectId(e.target.value)}
              >
                <option value="">-- Выберите объект для добавления --</option>
                {allObjects.map((obj) => (
                  <option key={obj.id} value={obj.id}>
                    {obj.code ? `[${obj.code}] ` : ''}{obj.name} {obj.branch_name ? `(${obj.branch_name})` : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-primary"
                onClick={handleAddObjectAccess}
                disabled={!selectedObjectId}
              >
                + Добавить
              </button>
            </div>

            <div style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#334155' }}>
              Пакетное добавление по филиалу:
            </div>
            <div className="access-branch-action">
              <select
                value={selectedBranchIdForBatch}
                onChange={(e) => setSelectedBranchIdForBatch(e.target.value)}
              >
                <option value="">-- Выберите филиал для добавления всех его объектов --</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} {b.code ? `(${b.code})` : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleAddBranchObjectsAccess}
                disabled={!selectedBranchIdForBatch}
              >
                Добавить все объекты
              </button>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeAccessModal}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
